import { t } from "@/i18n-js/instance";
import { useChangePlanModal } from "@circle-react/components/Modals/MemberProfileModal/Billing/Tabs/SubscriptionTab/ChangePlan/ChangePlanModal";
import { doesBelongToMobilePlatform } from "@circle-react/helpers/communityMemberSubscriptionHelpers";
import { useApplyCouponModal } from "@circle-react-shared/Paywalls/Modals/ApplyCouponModal";
import { useBlockedActionInfoModal } from "@circle-react-shared/Paywalls/Modals/BlockedActionInfoModal";
import { useCancelAndRefundPaywallSubscriptionModal } from "@circle-react-shared/Paywalls/Modals/CancelAndRefundPaywallSubscriptionModal";
import { usePaywallSubscriptionDetailsModal } from "@circle-react-shared/Paywalls/Modals/PaywallSubscriptionDetailsModal";
import { useResumePaywallSubscriptionModal } from "@circle-react-shared/Paywalls/Modals/ResumePaywallSubscriptionModal";
import { useAttributeSaleMenuItem } from "@circle-react-shared/Paywalls/hooks/useAttributeSaleMenuItem";
import { usePaywallSubscriptionListContext } from "../../../PaywallSubscriptionListContextProvider";

const defaultPolicies = {
  can_change_billing_cycle: false,
  can_admin_upgrade: false,
  can_trial_subscription_change_plan: false,
  can_resume: false,
  can_change_plan: false,
  can_cancel: false,
};

interface MenuItemProp {
  key: string;
  label: string;
  onClick: (event: any) => void;
}

const findActionModalOnClickFunction = ({
  subscription,
  actionModal,
  actionModalAttributes,
  blockedActionInfoModal,
  blockedActionInfoModalAttributes,
}: any) =>
  doesBelongToMobilePlatform(subscription)
    ? (e: any) => {
        blockedActionInfoModal.show(blockedActionInfoModalAttributes);
        e.stopPropagation();
      }
    : (e: any) => {
        actionModal.show(actionModalAttributes);
        e.stopPropagation();
      };

const buildMenuItems = ({
  subscription,
  subscriptionDetailModal,
  changePlanModal,
  resumeSubscriptionModal,
  cancelAndRefundSubscriptionModal,
  applyCouponModal,
  attributeSaleMenuItem,
  blockedActionInfoModal,
  onSuccess,
}: any) => {
  const menuItems: MenuItemProp[] = [];
  const {
    can_change_billing_cycle: canChangeBillingCycle,
    can_admin_upgrade: canUpgrade,
    can_trial_subscription_change_plan: canTrialSubscriptionChangePlan,
    can_resume: canResume,
    can_change_plan: canChangePlan,
    can_cancel: canCancel,
    can_apply_coupon: canApplyCoupon,
    can_remove_coupon: canRemoveCoupon,
  } = subscription.policies ?? defaultPolicies;

  menuItems.push({
    key: "subscription-details",
    label: t(
      "settings.paywalls_admin_dashboard.subscriptions.list.dropdown_menu.details",
    ),
    onClick: e => {
      subscriptionDetailModal.show({ subscription });
      e.stopPropagation();
    },
  });

  if (canApplyCoupon) {
    menuItems.push({
      key: "apply-coupon",
      label: t(
        "settings.paywalls_admin_dashboard.subscriptions.list.dropdown_menu.apply_coupon",
      ),
      onClick: findActionModalOnClickFunction({
        subscription,
        actionModal: applyCouponModal,
        actionModalAttributes: { subscription },
        blockedActionInfoModal,
        blockedActionInfoModalAttributes: {
          title: t("paywalls.apply_coupon_modal.select_coupon_step.title"),
          info: t(
            `paywalls.apply_coupon_modal.select_coupon_step.blocked_action_info.platform.${subscription.platform}`,
          ),
        },
      }),
    });
  }

  if (canRemoveCoupon) {
    menuItems.push({
      key: "remove-coupon",
      label: t(
        "settings.paywalls_admin_dashboard.subscriptions.list.dropdown_menu.remove_coupon",
      ),
      onClick: findActionModalOnClickFunction({
        subscription,
        actionModal: applyCouponModal,
        actionModalAttributes: { subscription, shouldRemoveCoupon: true },
        blockedActionInfoModal,
        blockedActionInfoModalAttributes: {
          title: t("paywalls.apply_coupon_modal.preview_coupon_step.title"),
          info: t(
            `paywalls.apply_coupon_modal.preview_coupon_step.blocked_action_info.platform.${subscription.platform}`,
          ),
        },
      }),
    });
  }

  if (attributeSaleMenuItem) menuItems.push(attributeSaleMenuItem);

  if (canChangeBillingCycle) {
    menuItems.push({
      key: "subscription-change-billing-cycle",
      label: t(
        "member_profile_modal.billing.subscriptions.actions.change_billing_cycle",
      ),
      onClick: findActionModalOnClickFunction({
        subscription,
        actionModal: changePlanModal,
        actionModalAttributes: {
          refetchSubscriptions: onSuccess,
          subscription: subscription,
          changePlan: false,
          isManagingMember: true,
        },
        blockedActionInfoModal,
        blockedActionInfoModalAttributes: {
          title: t(
            "member_profile_modal.billing.subscriptions.headings.billing_cycle",
          ),
          info: t(
            `settings.paywalls_admin_dashboard.subscriptions.change_plan_modal.billing_cycle.blocked_action_info.platform.${subscription.platform}`,
          ),
        },
      }),
    });
  }

  if (canChangePlan) {
    menuItems.push({
      key: "subscription-change-plan",
      label: t(
        "member_profile_modal.billing.subscriptions.actions.change_plan",
      ),
      onClick: findActionModalOnClickFunction({
        subscription,
        actionModal: changePlanModal,
        actionModalAttributes: {
          refetchSubscriptions: onSuccess,
          subscription: subscription,
          changePlan: true,
          isManagingMember: true,
        },
        blockedActionInfoModal,
        blockedActionInfoModalAttributes: {
          title: t(
            "member_profile_modal.billing.subscriptions.headings.change_plan",
          ),
          info: t(
            `settings.paywalls_admin_dashboard.subscriptions.change_plan_modal.change_plan.blocked_action_info.platform.${subscription.platform}`,
          ),
        },
      }),
    });
  }

  if (canUpgrade) {
    menuItems.push({
      key: "subscription-trial-upgrade",
      label: t("member_profile_modal.billing.subscriptions.actions.upgrade"),
      onClick: findActionModalOnClickFunction({
        subscription,
        actionModal: changePlanModal,
        actionModalAttributes: {
          refetchSubscriptions: onSuccess,
          subscription,
          changePlan: canTrialSubscriptionChangePlan,
          isManagingMember: true,
        },
        blockedActionInfoModal,
        blockedActionInfoModalAttributes: {
          title: t(
            "member_profile_modal.billing.subscriptions.headings.upgrade_plan",
          ),
          info: t(
            `settings.paywalls_admin_dashboard.subscriptions.change_plan_modal.upgrade_plan.blocked_action_info.platform.${subscription.platform}`,
          ),
        },
      }),
    });
  }

  if (canResume) {
    menuItems.push({
      key: "resume-subscription",
      label: t(
        "settings.paywalls_admin_dashboard.subscriptions.list.dropdown_menu.resume",
      ),
      onClick: findActionModalOnClickFunction({
        subscription,
        actionModal: resumeSubscriptionModal,
        actionModalAttributes: {
          subscription,
          onSuccess: onSuccess,
        },
        blockedActionInfoModal,
        blockedActionInfoModalAttributes: {
          title: t(
            "settings.paywalls_admin_dashboard.subscriptions.resume_subscription_modal.title",
          ),
          info: t(
            `settings.paywalls_admin_dashboard.subscriptions.resume_subscription_modal.blocked_action_info.platform.${subscription.platform}`,
          ),
        },
      }),
    });
  }

  if (canCancel) {
    menuItems.push({
      key: "cancel-and-refund-subscription",
      label: t(
        "settings.paywalls_admin_dashboard.subscriptions.list.dropdown_menu.cancel",
      ),
      onClick: findActionModalOnClickFunction({
        subscription,
        actionModal: cancelAndRefundSubscriptionModal,
        actionModalAttributes: {
          subscription,
          onSuccess: onSuccess,
        },
        blockedActionInfoModal,
        blockedActionInfoModalAttributes: {
          title: t(
            "settings.paywalls_admin_dashboard.subscriptions.cancel_and_refund_modal.title",
          ),
          info: t(
            `settings.paywalls_admin_dashboard.subscriptions.cancel_and_refund_modal.blocked_action_info.platform.${subscription.platform}`,
          ),
        },
      }),
    });
  }

  return menuItems;
};

export const useMenuItems = (cellContext: any) => {
  const {
    row: { original: subscription },
  } = cellContext;

  const { variant: variantSubscriptionList, refetchAllSubscriptions } =
    usePaywallSubscriptionListContext();

  const changePlanModal = useChangePlanModal();
  const cancelAndRefundSubscriptionModal =
    useCancelAndRefundPaywallSubscriptionModal();
  const subscriptionDetailModal = usePaywallSubscriptionDetailsModal(
    variantSubscriptionList,
  );
  const resumeSubscriptionModal = useResumePaywallSubscriptionModal();
  const attributeSaleMenuItem = useAttributeSaleMenuItem({
    attributable: subscription,
    attributableType: "CommunityMemberSubscription",
    onSuccess: refetchAllSubscriptions,
  });
  const applyCouponModal = useApplyCouponModal();
  const blockedActionInfoModal = useBlockedActionInfoModal();

  const menuItems = buildMenuItems({
    subscription,
    changePlanModal,
    cancelAndRefundSubscriptionModal,
    subscriptionDetailModal,
    resumeSubscriptionModal,
    applyCouponModal,
    attributeSaleMenuItem,
    blockedActionInfoModal,
    onSuccess: refetchAllSubscriptions,
  });

  return {
    menuItems,
  };
};
